import { useState } from 'react';
import { Navbar, Nav, Container, NavDropdown } from 'react-bootstrap';
import { FaSignInAlt, FaSignOutAlt } from 'react-icons/fa';
import { LinkContainer } from 'react-router-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { useLogoutMutation } from '../slices/usersApiSlice';
import { logout } from '../slices/authSlice';
import { useFetchAthleteByUserIdQuery } from '../slices/athleteApiSlice';
import { useGetDraftByLeagueQuery } from '../slices/draftApiSlice';
import { useTheme } from '../contexts/ThemeContext';
import { buildDraftRoomPath } from '../utils/draftRoomMode';
import { clearStoredActiveLeagueId, getLeaguePath, useActiveLeagueId } from '../utils/activeLeague';

const formatDraftDateTime = (dateValue) => {
  if (!dateValue) return null;

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const Header = () => {
  const [expanded, setExpanded] = useState(false);
  const { userInfo } = useSelector((state) => state.auth);
  const storedActiveLeagueId = useActiveLeagueId();
  const athleteRequest = storedActiveLeagueId
    ? { userId: userInfo?._id, leagueId: storedActiveLeagueId }
    : userInfo?._id;
  const { data: athlete } = useFetchAthleteByUserIdQuery(athleteRequest, {
    skip: !userInfo,
  });
  const leagueId = athlete?.league?._id || athlete?.league;
  const leaguePath = getLeaguePath(leagueId || storedActiveLeagueId);
  const { data: leagueDraft } = useGetDraftByLeagueQuery(leagueId, {
    skip: !leagueId,
  });
  const { isDarkMode } = useTheme();

  const dispatch = useDispatch();
  const [logoutApiCall] = useLogoutMutation();

  const logoutHandler = async () => {
    try {
      await logoutApiCall().unwrap();
      clearStoredActiveLeagueId();
      dispatch(logout());
      setExpanded(false);
    } catch (err) {
      console.error(err);
    }
  };

  const isSameLocalDate = (a, b) =>
    a?.getFullYear?.() === b?.getFullYear?.() &&
    a?.getMonth?.() === b?.getMonth?.() &&
    a?.getDate?.() === b?.getDate?.();

  const draftStartDate = leagueDraft?.startTime ? new Date(leagueDraft.startTime) : null;
  const scheduledDraftLabel = leagueDraft?.status === 'scheduled'
    ? formatDraftDateTime(leagueDraft.startTime)
    : null;
  const showEnterDraftLink =
    Boolean(leagueDraft?._id) &&
    Boolean(draftStartDate && !Number.isNaN(draftStartDate.getTime())) &&
    isSameLocalDate(draftStartDate, new Date()) &&
    (leagueDraft.status === 'scheduled' || leagueDraft.status === 'active');

  return (
    <header style={{ position: 'relative', zIndex: 1050 }}>
      <Navbar
        className="navbar-modern"
        variant={isDarkMode ? 'dark' : 'light'}
        expand="lg"
        collapseOnSelect
        expanded={expanded}
        onToggle={(nextExpanded) => setExpanded(nextExpanded)}
      >
        <Container fluid className="px-0">
          <LinkContainer to="/">
            <Navbar.Brand className="text-primary me-0">
              <img className="site-brand-logo" src="/logo_vector.svg" alt="League Runner" />
              <span>
                <span className="site-brand">League Runner</span>
                <span className="site-brand-tag">You are the Athlete</span>
              </span>
            </Navbar.Brand>
          </LinkContainer>

          <div className="header-quick-actions mx-auto">
            <ul className="navbar-nav d-flex flex-row gap-2 align-items-center">
              {showEnterDraftLink && (
                <li className="nav-item">
                  <LinkContainer to={buildDraftRoomPath(leagueDraft._id, { chooseView: true })}>
                    <button className="btn btn-sm-modern header-draft-button" type="button">
                      Enter Draft
                    </button>
                  </LinkContainer>
                </li>
              )}
              {!showEnterDraftLink && scheduledDraftLabel && (
                <li className="nav-item text-secondary small fw-semibold">
                  Draft {scheduledDraftLabel}
                </li>
              )}
            </ul>
          </div>

          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="ms-auto align-items-center gap-2">
              {athlete ? (
                <>
                  <Nav.Link
                    as={Link}
                    to={leaguePath}
                    className="header-auth-link"
                    onClick={() => setExpanded(false)}
                  >
                    League
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/team"
                    className="header-auth-link"
                    onClick={() => setExpanded(false)}
                  >
                    Team
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/activities"
                    className="header-auth-link"
                    onClick={() => setExpanded(false)}
                  >
                    Activities
                  </Nav.Link>
                </>
              ) : (
                <>
                  <Nav.Link
                    as={Link}
                    to="/get-started"
                    className="header-auth-link"
                    onClick={() => setExpanded(false)}
                  >
                    Get Started
                  </Nav.Link>
                  <Nav.Link
                    as={Link}
                    to="/login"
                    className="header-auth-link"
                    onClick={() => setExpanded(false)}
                  >
                    <FaSignInAlt className="me-1" /> Sign In
                  </Nav.Link>
                </>
              )}

              {athlete ? (
                <NavDropdown
                  title={<span className="text-primary">{athlete.firstName} {athlete.lastName}</span>}
                  id="athlete"
                  align="end"
                >
                  <LinkContainer to="/profile">
                    <NavDropdown.Item>Profile</NavDropdown.Item>
                  </LinkContainer>
                  <LinkContainer to="/dashboard">
                    <NavDropdown.Item>My Dashboard</NavDropdown.Item>
                  </LinkContainer>
                  {athlete.isAdmin && (
                    <>
                      <LinkContainer to="/admin">
                        <NavDropdown.Item>Admin Dashboard</NavDropdown.Item>
                      </LinkContainer>
                      <LinkContainer to="/admin/news">
                        <NavDropdown.Item>News Publishing</NavDropdown.Item>
                      </LinkContainer>
                      <LinkContainer to="/admin/errors">
                        <NavDropdown.Item>Error Logs</NavDropdown.Item>
                      </LinkContainer>
                      <LinkContainer to="/admin/garmin">
                        <NavDropdown.Item>Garmin Diagnostics</NavDropdown.Item>
                      </LinkContainer>
                      <LinkContainer to="/test-admin">
                        <NavDropdown.Item>Test Admin</NavDropdown.Item>
                      </LinkContainer>
                      {userInfo?.email === 'ethan@leaguerunner.net' && (
                        <LinkContainer to="/testing">
                          <NavDropdown.Item>Testing</NavDropdown.Item>
                        </LinkContainer>
                      )}
                    </>
                  )}
                  <NavDropdown.Item onClick={logoutHandler}>Logout</NavDropdown.Item>
                </NavDropdown>
              ) : (
                <Link
                  className="btn btn-primary-modern btn-sm-modern"
                  to="/register"
                  role="button"
                  onClick={() => setExpanded(false)}
                >
                  <FaSignOutAlt className="me-1" /> Sign Up
                </Link>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
    </header>
  );
};

export default Header;
